import { inspect } from "node:util";

const LEVELS = ["debug", "info", "warn", "error"] as const;
export type LogLevel = (typeof LEVELS)[number];

const LEVEL_COLOR: Record<LogLevel, string> = {
  debug: "\x1b[2m", // dim
  info: "\x1b[36m", // cyan
  warn: "\x1b[33m", // yellow
  error: "\x1b[31m", // red
};
const RESET = "\x1b[0m";
const DIM = "\x1b[2m";
const SCOPE = "\x1b[35m"; // magenta

function envLevel(): LogLevel {
  const raw = (process.env.REVUIW_LOG_LEVEL ?? "").toLowerCase();
  return (LEVELS as readonly string[]).includes(raw) ? (raw as LogLevel) : "info";
}

function useColor(): boolean {
  if (process.env.NO_COLOR) return false;
  const force = process.env.FORCE_COLOR;
  if (force !== undefined) return force !== "0" && force.toLowerCase() !== "false";
  return process.stderr.isTTY === true;
}

function timestamp(): string {
  // HH:MM:SS.mmm — enough to reason about how long a stage took.
  return new Date().toISOString().slice(11, 23);
}

export function formatDuration(ms: number): string {
  if (ms < 1_000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1_000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m${rest.toString().padStart(2, "0")}s`;
}

/**
 * Small structured logger for the runner. Everything goes to stderr so stdout
 * stays reserved for machine-readable output. Verbosity is controlled by the
 * REVUIW_LOG_LEVEL env var (debug | info | warn | error; default info).
 */
export class Logger {
  constructor(
    private readonly scope: string,
    private readonly threshold: LogLevel = envLevel(),
  ) {}

  child(scope: string): Logger {
    return new Logger(this.scope ? `${this.scope}:${scope}` : scope, this.threshold);
  }

  debug(message: string, ...details: unknown[]): void { this.emit("debug", message, details); }
  info(message: string, ...details: unknown[]): void { this.emit("info", message, details); }
  warn(message: string, ...details: unknown[]): void { this.emit("warn", message, details); }
  error(message: string, ...details: unknown[]): void { this.emit("error", message, details); }

  /**
   * Log the start of a stage and return a callback that logs its completion
   * with the elapsed time appended.
   */
  step(startMessage: string): (doneMessage?: string) => void {
    const start = Date.now();
    this.info(startMessage);
    return (doneMessage) => {
      const elapsed = formatDuration(Date.now() - start);
      this.info(`${doneMessage ?? `${startMessage} done`} (${elapsed})`);
    };
  }

  private emit(level: LogLevel, message: string, details: unknown[]): void {
    if (LEVELS.indexOf(level) < LEVELS.indexOf(this.threshold)) return;
    const color = useColor();
    const time = color ? `${DIM}${timestamp()}${RESET}` : timestamp();
    const tag = color ? `${LEVEL_COLOR[level]}${level.padEnd(5)}${RESET}` : level.padEnd(5);
    const scope = this.scope ? (color ? `${SCOPE}${this.scope}${RESET} ` : `${this.scope} `) : "";
    const suffix = details.length
      ? " " + details.map((d) => (typeof d === "string" ? d : inspect(d, { depth: 4, colors: color }))).join(" ")
      : "";
    process.stderr.write(`${time} ${tag} ${scope}${message}${suffix}\n`);
  }
}

export const logger = new Logger("revuiw");

/**
 * Emit a periodic "still running" line so long-lived child processes visibly
 * make progress instead of hanging silently. Returns a stop function.
 */
export function heartbeat(log: Logger, label: string, intervalMs = 15_000): () => void {
  const start = Date.now();
  const timer = setInterval(() => {
    log.info(`${label} still running (${formatDuration(Date.now() - start)} elapsed)`);
  }, intervalMs);
  timer.unref?.();
  return () => clearInterval(timer);
}

/**
 * Wrap a per-line callback in a chunk consumer that buffers partial lines, so
 * streamed stderr can be logged one clean line at a time. Blank lines are
 * dropped. The trailing partial line (if any) is delivered on flush().
 */
export function lineStreamer(onLine: (line: string) => void): {
  push(chunk: string): void;
  flush(): void;
} {
  let buffer = "";
  return {
    push(chunk: string): void {
      buffer += chunk;
      let index: number;
      while ((index = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, index).replace(/\r$/, "");
        buffer = buffer.slice(index + 1);
        if (line.trim()) onLine(line);
      }
    },
    flush(): void {
      const line = buffer.trim();
      buffer = "";
      if (line) onLine(line);
    },
  };
}
